import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { FiPhone, FiMail, FiMapPin, FiClock, FiFacebook, FiInstagram, FiTwitter, FiLogIn, FiLogOut, FiImage, FiInfo, FiBarChart3 } = FiIcons;

const Contact = () => {
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Contact form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: ['+974 4433 2211'],
      color: 'bg-blue-500'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: ['admin@easyaidev.com'],
      color: 'bg-green-500'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: ['Al Mana Tower B,', '231 Suhaim Bin Hamad St,', 'Doha, Qatar'],
      color: 'bg-red-500'
    },
    {
      icon: FiClock,
      title: 'Hours',
      details: ['9:00 AM - 10:00 PM', 'Friday: 4:00 PM - 10:00 PM', 'ساعات العمل من ٩ص حتى ١٠م', 'يوم الجمعة من ٤م حتى ١٠م'],
      color: 'bg-purple-500'
    }
  ];

  const socialMedia = [
    {
      icon: FiFacebook,
      name: 'Facebook',
      url: '#',
      color: 'text-blue-600'
    },
    {
      icon: FiInstagram,
      name: 'Instagram',
      url: '#',
      color: 'text-pink-600'
    },
    {
      icon: FiTwitter,
      name: 'Twitter',
      url: '#',
      color: 'text-blue-400'
    }
  ];

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-maroon-600 to-maroon-800 text-white relative">
        {/* Navigation Tabs */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center space-x-2">
            {/* Dashboard button - only show if user is logged in */}
            {user && (
              <Link
                to="/dashboard"
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
              >
                <SafeIcon icon={FiBarChart3} className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
            )}

            {/* About Us tab */}
            <Link
              to="/about"
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>About Us</span>
            </Link>

            {/* Gallery tab */}
            <Link
              to="/gallery"
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
            >
              <SafeIcon icon={FiImage} className="w-4 h-4" />
              <span>Gallery</span>
            </Link>

            {/* Login/Logout button */}
            {user ? (
              <button
                onClick={logout}
                className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>Logout</span>
              </button>
            ) : (
              <Link
                to="/login"
                className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogIn} className="w-4 h-4" />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-maroon-100 max-w-3xl mx-auto">
              Get in touch with EasyAI Dental Center. We're here to help with all your dental needs.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Contact Information */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 text-center"
              >
                <div className={`w-16 h-16 ${info.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <SafeIcon icon={info.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-grey-900 mb-3">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-grey-600 mb-1">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Contact Form and Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-grey-900 mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">
                  Subject
                </label>
                <select
                  required
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                >
                  <option value="">Select a subject</option>
                  <option value="appointment">Book an Appointment</option>
                  <option value="general">General Inquiry</option>
                  <option value="emergency">Emergency</option>
                  <option value="insurance">Insurance Questions</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">
                  Message
                </label>
                <textarea
                  rows={5}
                  required
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full bg-maroon-600 hover:bg-maroon-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
              >
                Send Message
              </motion.button>
            </form>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="h-full min-h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.303463399432!2d51.5079208745997!3d25.27767854768244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2c65d55a1e9ea2bb%3A0xf5013f6e9b6d5a4b!2sEasy%20AI%20For%20Software%20And%20Web%20Development!5e1!3m2!1sen!2sqa!4v1764135165520!5m2!1sen!2sqa"
                width="585"
                height="630"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EasyAI Dental Center Location"
              />
            </div>
          </motion.div>

        </div>

        {/* Social Media */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <h2 className="text-2xl font-bold text-grey-900 mb-6">Follow Us</h2>
          <div className="flex justify-center space-x-6">
            {socialMedia.map((social) => (
              <motion.a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`w-12 h-12 ${social.color} hover:opacity-80 rounded-full flex items-center justify-center transition-all`}
              >
                <SafeIcon icon={social.icon} className="w-6 h-6" />
              </motion.a>
            ))}
          </div>
        </motion.section>

        {/* Emergency Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-16 bg-red-50 border border-red-200 rounded-lg p-6 text-center"
        >
          <h3 className="text-xl font-bold text-red-800 mb-2">Emergency Dental Care</h3>
          <p className="text-red-600 mb-4">
            For dental emergencies outside of business hours, please call our emergency line:
          </p>
          <a
            href="tel:+97455556666"
            className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5" />
            <span>+974 5555 6666</span>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;